import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardPageHeader from "../../components/Dashboard/DashboardPageHeader";
import { getDateRanges } from "../../i18n/dashboardHelpers";
import { getAdminOverview } from "../../services/apiService";

function getDateRange(days) {
  const end = new Date();
  end.setUTCHours(23, 59, 59, 999);

  const start = new Date();
  start.setUTCDate(start.getUTCDate() - days + 1);
  start.setUTCHours(0, 0, 0, 0);

  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

function extractOverviewData(response) {
  return response?.data ?? {};
}

function formatRate(rate) {
  if (rate == null || Number.isNaN(Number(rate))) {
    return null;
  }

  const numericRate = Number(rate);
  return {
    change: `${numericRate >= 0 ? "+" : ""}${numericRate}%`,
    direction: numericRate >= 0 ? "up" : "down",
  };
}

function formatChartLabel(value) {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function normalizeChartData(chartData) {
  if (!Array.isArray(chartData)) return [];

  return chartData.map((point) => ({
    date: formatChartLabel(point.date),
    value: Number(point.count ?? 0) || 0,
  }));
}

function getChartTicks(maxValue) {
  if (maxValue <= 1) return [0, 1];
  if (maxValue <= 5) {
    return Array.from({ length: maxValue + 1 }, (_, index) => index);
  }

  const tickStep = Math.max(Math.ceil(maxValue / 4), 1);
  const tickCount = Math.ceil(maxValue / tickStep);
  return Array.from({ length: tickCount + 1 }, (_, index) => index * tickStep);
}

function getProviderName(provider) {
  return provider.full_name || provider.company_name || provider.email || "—";
}

function getInitials(name = "") {
  const parts = name.replace(/_/g, " ").split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("");
}

function avatarColor(value = "") {
  const colors = ["#DBEAFE", "#D1FAE5", "#FEF3C7", "#FCE7F3", "#E5E7EB"];
  const index = value.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) % colors.length;
  return colors[index];
}

function TrendBadge({ change, direction = "up" }) {
  const isPositive = direction === "up";
  return (
    <span className={`dashboard-trend ${isPositive ? "" : "dashboard-trend--down"}`}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        {isPositive ? (
          <path
            d="M7 14l4-4 3 3 5-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M7 10l4 4 3-3 5 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
      {change}
    </span>
  );
}

function StatCard({ label, value, trend }) {
  return (
    <div className="dashboard-stat-card">
      <p className="dashboard-stat-card__label">{label}</p>
      <div className="dashboard-stat-card__row">
        <p className="dashboard-stat-card__value">{value}</p>
        {trend ? <TrendBadge change={trend.change} direction={trend.direction} /> : null}
      </div>
    </div>
  );
}

function SimsOverTimeChart({ data = [], emptyLabel, chartAria }) {
  const width = 560;
  const height = 200;
  const padding = { top: 20, right: 20, bottom: 30, left: 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const maxValue = Math.max(...data.map((item) => item.value), 1);
  const ticks = getChartTicks(maxValue);

  if (data.length === 0) {
    return <p className="dashboard-list__item">{emptyLabel}</p>;
  }

  const points = data.map((item, index) => {
    const x =
      data.length === 1
        ? padding.left + chartWidth / 2
        : padding.left + (index / (data.length - 1)) * chartWidth;
    const y = padding.top + chartHeight - (item.value / maxValue) * chartHeight;
    return { x, y, ...item, key: `${item.date}-${index}` };
  });

  const linePath = points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");

  const areaPath = `${linePath} L ${points[points.length - 1].x} ${
    padding.top + chartHeight
  } L ${points[0].x} ${padding.top + chartHeight} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="dashboard-chart__svg"
      role="img"
      aria-label={chartAria}
    >
      {ticks.map((tick) => {
        const y = padding.top + chartHeight - (tick / maxValue) * chartHeight;
        return (
          <g key={tick}>
            <line
              x1={padding.left}
              y1={y}
              x2={width - padding.right}
              y2={y}
              stroke="#E5E7EB"
              strokeWidth="1"
            />
            <text
              x={padding.left - 8}
              y={y + 4}
              textAnchor="end"
              fontSize="11"
              fill="#9CA3AF"
            >
              {tick}
            </text>
          </g>
        );
      })}
      <path d={areaPath} fill="url(#simsGradient)" opacity="0.3" />
      <path
        d={linePath}
        fill="none"
        stroke="#003FB1"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {points.map((point) => (
        <circle
          key={point.key}
          cx={point.x}
          cy={point.y}
          r="4"
          fill="#003FB1"
          stroke="#fff"
          strokeWidth="2"
        />
      ))}
      {points.map((point) => (
        <text
          key={`label-${point.key}`}
          x={point.x}
          y={height - 8}
          textAnchor="middle"
          fontSize="11"
          fill="#9CA3AF"
        >
          {point.date}
        </text>
      ))}
      <defs>
        <linearGradient id="simsGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#003FB1" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#003FB1" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function Overview() {
  const { t } = useTranslation();
  const dateRanges = useMemo(() => getDateRanges(t), [t]);
  const [dateRange, setDateRange] = useState("7");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const selectedRange = dateRanges.find((range) => range.value === dateRange) ?? dateRanges[0];
  const { startDate, endDate } = useMemo(
    () => getDateRange(selectedRange.days),
    [selectedRange.days]
  );

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getAdminOverview({ startDate, endDate });
      setOverview(extractOverviewData(response));
    } catch (fetchError) {
      setOverview(null);
      setError(fetchError?.response?.data?.message || t("dashboard.overview.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, t]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const stats = useMemo(() => overview?.stats ?? {}, [overview]);
  const rates = useMemo(() => stats.rate ?? {}, [stats]);
  const topProviders = overview?.top_providers ?? [];
  const chartData = normalizeChartData(overview?.esims_over_time);

  const statCards = useMemo(
    () => [
      {
        label: t("dashboard.overview.totalProviders"),
        value: loading ? "—" : Number(stats.total_providers ?? 0).toLocaleString("en-US"),
        trend: null,
      },
      {
        label: t("dashboard.overview.totalEsims"),
        value: loading ? "—" : Number(stats.total_esims ?? 0).toLocaleString("en-US"),
        trend: loading ? null : formatRate(rates.total_esims),
      },
      {
        label: t("dashboard.overview.activeEsims"),
        value: loading ? "—" : Number(stats.active_esims ?? 0).toLocaleString("en-US"),
        trend: loading ? null : formatRate(rates.active_esims),
      },
      {
        label: t("dashboard.overview.pendingRequests"),
        value: loading ? "—" : Number(stats.pending_requests ?? 0).toLocaleString("en-US"),
        trend: loading ? null : formatRate(rates.pending_requests),
      },
    ],
    [loading, rates, stats, t]
  );

  const chartMetricValue = loading
    ? "—"
    : Number(stats.total_esims ?? 0).toLocaleString("en-US");
  const chartMetricRate = formatRate(rates.total_esims);
  const periodLabel = t("dashboard.common.vsPreviousDays", { days: selectedRange.days });

  return (
    <div className="dashboard-page">
      <DashboardPageHeader title={t("dashboard.overview.title")}>
        <select
          className="dashboard-select"
          value={dateRange}
          onChange={(event) => setDateRange(event.target.value)}
          aria-label={t("dashboard.common.dateRange")}
        >
          {dateRanges.map((range) => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </select>
      </DashboardPageHeader>

      {error ? (
        <p className="settings-form-status settings-form-status--error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="dashboard-stats">
        {statCards.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>

      <div className="dashboard-grid dashboard-grid--2-1">
        <div className="dashboard-card">
          <div className="dashboard-card__header">
            <div>
              <h2 className="dashboard-card__title">{t("dashboard.overview.esimsOverTime")}</h2>
              <div className="dashboard-card__metric">
                <span className="dashboard-card__metric-value">{chartMetricValue}</span>
                {chartMetricRate ? (
                  <TrendBadge
                    change={chartMetricRate.change}
                    direction={chartMetricRate.direction}
                  />
                ) : null}
                <span className="dashboard-card__metric-sub">{periodLabel}</span>
              </div>
            </div>
          </div>
          <SimsOverTimeChart
            data={loading ? [] : chartData}
            emptyLabel={t("dashboard.overview.noChartData")}
            chartAria={t("dashboard.overview.chartAria")}
          />
        </div>

        <div className="dashboard-card">
          <h2 className="dashboard-card__title">{t("dashboard.overview.topPartners")}</h2>
          <ul className="dashboard-list">
            {loading ? (
              <li className="dashboard-list__item">{t("dashboard.overview.loadingProviders")}</li>
            ) : topProviders.length === 0 ? (
              <li className="dashboard-list__item">{t("dashboard.overview.noProviders")}</li>
            ) : (
              topProviders.map((provider) => {
                const name = getProviderName(provider);
                return (
                  <li key={provider.provider_id ?? name} className="dashboard-list__item">
                    <span className="dashboard-list__label">
                      <span
                        className="dashboard-table__avatar"
                        style={{
                          backgroundColor: avatarColor(name),
                          width: 28,
                          height: 28,
                          fontSize: 11,
                        }}
                        aria-hidden="true"
                      >
                        {getInitials(name)}
                      </span>
                      {name}
                    </span>
                    <span className="dashboard-list__value">
                      {Number(provider.total_esims ?? 0).toLocaleString("en-US")}
                    </span>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Overview;
